/**
 * CORE-1921 — какие правовые документы открывает маршрут `/legal/:docId`.
 *
 * Раньше незнакомый `docId` проваливался в последнюю ветку экрана и показывал
 * Practitioner Streaming Agreement: «Пользовательское соглашение» в подвале
 * лендинга и «Условия использования» в карточке подписки сторов вели на
 * документ `terms-of-service`, такого документа нет — и человек читал чужой
 * договор. Сторож `legalDocLinks-CORE1921` держит, что каждый адрес документа
 * в коде есть в этом списке.
 *
 * Теперь незнакомый адрес — это «Документ не найден», а не документ по
 * умолчанию. Так же ведёт себя `/legal/ai-notice` в сборке, где уведомления
 * ещё нет: его id просто нет в этом списке.
 */
export const LEGAL_DOC_IDS = [
  'privacy-policy',
  'streaming-agreement',
  'delete-account',
  'terms-of-use',
  'mission',
  'ethics-code',
  'ai-notice',
  // CORE-1928 — полные тексты согласий: на рекламу и на обработку ПДн.
  'marketing-consent',
  'personal-data-consent',
  // CORE-2099 — агентский договор мастера (АО): адрес оферты из его преамбулы.
  // Текста в сборке нет — действующая редакция берётся из реестра на сервере.
  'master-agency-agreement',
] as const;

export type LegalDocId = (typeof LEGAL_DOC_IDS)[number];

/** Знакомый id документа — или `null`, если такого документа нет. */
export const resolveLegalDocId = (raw: unknown): LegalDocId | null =>
  typeof raw === 'string' && (LEGAL_DOC_IDS as readonly string[]).includes(raw)
    ? (raw as LegalDocId)
    : null;

/**
 * CORE-1567 — ответ юриста 30.10 (вопрос 17): Practitioner Streaming Agreement
 * (AGAPE.IO Limited, право Англии, арбитраж ОАЭ) — только для мастеров вне
 * контура РФ. В российском контуре (правило `utils/legalJurisdiction`, то же, что
 * выбирает редакцию соглашения и строку реквизитов CORE-1934) ссылки на него нет
 * ни в одном подвале, а прямой адрес отвечает «Документ не найден».
 */
const FOREIGN_ONLY_DOC_IDS: readonly LegalDocId[] = ['streaming-agreement'];

/** Показывать ли документ человеку этого контура. */
export const isLegalDocShown = (docId: LegalDocId, foreignJurisdiction: boolean): boolean =>
  foreignJurisdiction || !FOREIGN_ONLY_DOC_IDS.includes(docId);

/**
 * Ссылки подвала, которые видит человек этого контура: ссылка на документ только
 * для зарубежного контура в российском убирается, остальные — как есть.
 */
export function shownFooterLinks<T extends { path?: string }>(links: readonly T[], foreignJurisdiction: boolean): T[] {
  return links.filter((link) => {
    const docId = link.path?.startsWith('/legal/') ? resolveLegalDocId(link.path.slice('/legal/'.length)) : null;
    return !docId || isLegalDocShown(docId, foreignJurisdiction);
  });
}

/** Текст экрана «не найден». Тон — спокойный, без восклицаний. */
export const LEGAL_DOC_NOT_FOUND_COPY = {
  title: 'Документ не найден',
  body: 'Такого документа у нас нет. Возможно, ссылка устарела или документ ещё не опубликован.',
  termsLink: 'Пользовательское соглашение',
  homeLink: 'На главную',
} as const;
